import axios, { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import { clearKioskDevice, getKioskDevice, isKioskDevicePath } from './kioskDevice';
import type { ApiResponse, PaginatedResponse } from '@/types';
import { formatDetailList } from '@/utils/validation';
import { queueWarning, showWarning, warningsFrom } from '@/utils/warnings';
import { notifyWrite } from '@/utils/refreshAfterWrite';

// ---------------------------------------------------------------------------
// Axios instance
// ---------------------------------------------------------------------------

export const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

/**
 * Absolute URL for an API path, for the few places that must use `fetch`
 * directly (PDF blobs, public pages that run before axios has a token).
 *
 * A relative `/api/...` is not a smaller version of this — it is a different
 * request to a different server. The SPA is served from its own host, whose
 * rewrite sends every unmatched path to index.html, so a relative call returns
 * `200 text/html` and `res.ok` is true. The failure then surfaces wherever the
 * body is first read, as "Unexpected token '<'", which is what visitors saw on
 * the QR check-in page and what silently blanked the badge preview.
 *
 * `path` is the part after the origin and includes `/api`.
 */
export function apiUrl(path: string): string {
  return `${API_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
}

/**
 * The sentence the backend sent, from a raw `fetch` Response.
 *
 * `apiGet`/`apiPost` get this from the axios interceptor; direct `fetch`
 * callers were each inventing their own message and discarding `detail`.
 */
export async function fetchErrorMessage(res: Response, fallback: string): Promise<string> {
  const body = await res.text().catch(() => '');
  if (body.trimStart().startsWith('{')) {
    try {
      const detail = (JSON.parse(body) as { detail?: unknown }).detail;
      if (typeof detail === 'string' && detail.trim()) return detail;
      if (Array.isArray(detail)) {
        const msg = formatDetailList(detail);
        if (msg) return msg;
      }
    } catch {
      // fall through to the fallback
    }
  }
  return `${fallback} (HTTP ${res.status})`;
}

const api = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
  // 🔴 STAYS AT 30s, DELIBERATELY. This is the page-load budget: a read that
  // has not answered in thirty seconds has failed, and the failure should be
  // visible. In particular a hibernated Render service takes 64-162s to wake,
  // and raising this globally would turn that into a two-minute spinner on
  // every screen instead of an error — hiding the one fault most worth seeing.
  //
  // A BULK WRITE IS NOT A PAGE LOAD and opts out per call. See BULK_WRITE_TIMEOUT_MS.
  timeout: 30_000,
});

/**
 * Timeout for a single large write, passed per call — never set globally.
 *
 * Bulk desk creation offers 500 in both the schema and the form, and could not
 * honour it: the endpoint flushed once per desk, so 500 desks was 501 network
 * round trips — 66.97s measured against the deployment — and the browser
 * aborted at 30s. The server fix (one flush for the batch, 501 round trips ->
 * 2) is the real repair and shipped FIRST, deliberately, so this timeout never
 * had to cover the 67s case. Re-measured against the deployed fix, 500 desks
 * is 3-6s. This is the margin around that, not a cover for slowness.
 *
 * 90 seconds, chosen between two bounds:
 *   * an order of magnitude above the measured 3-6s, so an unusually loaded
 *     moment does not fail a write the user has already waited for;
 *   * and below the 64-162s cold-start range, so a hibernated service still
 *     FAILS rather than being waited out. That is the property the global
 *     timeout exists to preserve, and this must not undo it.
 *
 * Use it for writes whose size the user chose. Not for reads.
 */
export const BULK_WRITE_TIMEOUT_MS = 90_000;

// ---------------------------------------------------------------------------
// Token helpers (import lazily to avoid circular deps with auth store)
// ---------------------------------------------------------------------------

/** Exported so other modules never have to name the storage key themselves.
 *  That key is one of the few strings that legitimately differs between the
 *  two deployments, and a second copy of it in a new file is a mirroring bug
 *  waiting to happen. */
export function getAccessToken(): string | null {
  try {
    const raw = localStorage.getItem('spaciora-auth');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { state?: { accessToken?: string } };
    return parsed?.state?.accessToken ?? null;
  } catch {
    return null;
  }
}

/** The signed-in person's user id, read from the same persisted session as the
 *  token (FM Technician A16: the offline queue is kept per person, so it has to
 *  know whose it is without importing the auth store). Null when signed out. */
export function getSignedInUserId(): string | null {
  try {
    const raw = localStorage.getItem('spaciora-auth');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { state?: { user?: { id?: string } | null; accessToken?: string } };
    if (!parsed?.state?.accessToken) return null;
    return parsed?.state?.user?.id ?? null;
  } catch {
    return null;
  }
}

function getRefreshToken(): string | null {
  try {
    const raw = localStorage.getItem('spaciora-auth');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { state?: { refreshToken?: string } };
    return parsed?.state?.refreshToken ?? null;
  } catch {
    return null;
  }
}

function clearAuth(): void {
  localStorage.removeItem('spaciora-auth');
}

// ---------------------------------------------------------------------------
// Request interceptor — attach JWT
// ---------------------------------------------------------------------------

api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Reception R4: the tablet's own credential, and only on its own routes.
    const token = isKioskDevicePath(config.url) ? getKioskDevice()?.token : getAccessToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => Promise.reject(error),
);

// ---------------------------------------------------------------------------
// Response interceptor — handle 401 refresh flow
// ---------------------------------------------------------------------------

/**
 * ONE refresh at a time (Deep-test EXT-23).
 *
 * Rotation revokes the refresh token it is given, so two refreshes sent with
 * the same token cannot both succeed: the server honours the first and answers
 * the second 401 — and a refused refresh clears the session and goes to
 * /login, signing the person out of the session the first had just renewed.
 * The old `isRefreshing` flag queued 401s only WHILE a refresh was in flight,
 * so a request sent with the old token whose 401 landed a moment after the
 * refresh finished started a second one; authStore.refresh() was another path
 * with no queue at all; and two tabs at the same expiry each sent their own.
 *
 * So every refresh in the staff app goes through this function:
 *   - concurrent callers in this tab share one promise;
 *   - across tabs it runs under a Web Lock, and once it holds the lock it
 *     re-reads storage — if another tab rotated meanwhile, that tab's access
 *     token is used and nothing is spent;
 *   - the interceptor below does not refresh at all for a request that went
 *     out with an access token that has since been replaced.
 * The portal sessions have the same rule in utils/portalSession.ts.
 */
let refreshInFlight: Promise<string> | null = null;

export function refreshAccessToken(): Promise<string> {
  if (refreshInFlight) return refreshInFlight;
  const spent = getRefreshToken();

  const rotate = async (): Promise<string> => {
    const current = getRefreshToken();
    if (!current) throw new Error('Not signed in');
    // Another tab rotated while this one waited for the lock: its tokens are
    // already in storage, and the token this tab was about to send is revoked.
    if (spent && current !== spent) {
      const access = getAccessToken();
      if (access) return access;
    }
    // The API wraps payloads as { success, data: {...}, message }, so the
    // rotated tokens live under data.data.* — NOT data.* . Reading the wrong
    // level stored `undefined`, which silently logged active users out
    // ~15 min after login (at first access-token expiry). Read the nested shape.
    const { data: body } = await axios.post<{
      data: { access_token: string; refresh_token: string };
    }>(`${API_BASE_URL}/api/auth/refresh`, { refresh_token: current });
    const newAccessToken = body.data.access_token;
    const newRefreshToken = body.data.refresh_token;
    // Persist via the auth store so its in-memory state and the localStorage
    // copy stay in sync. A direct localStorage write would later be clobbered
    // when the store re-persists its (stale) in-memory tokens, breaking the
    // next refresh. Lazy import avoids a circular dependency with the store.
    const { useAuthStore } = await import('@/stores/authStore');
    useAuthStore.getState().setTokens(newAccessToken, newRefreshToken);
    return newAccessToken;
  };

  const locks = typeof navigator !== 'undefined'
    ? (navigator as Navigator & { locks?: LockManager }).locks
    : undefined;
  const run: Promise<string> = locks?.request
    // The lock resolves with rotate()'s promise; then() flattens it.
    ? locks.request('spaciora-auth-refresh', rotate).then((token) => token)
    : rotate();
  refreshInFlight = run;
  const clear = () => { if (refreshInFlight === run) refreshInFlight = null; };
  run.then(clear, clear);
  return run;
}

/** The bearer token a request actually went out with, or null. */
function tokenSentWith(config: InternalAxiosRequestConfig | undefined): string | null {
  const raw = config?.headers?.Authorization;
  if (typeof raw !== 'string' || !raw.startsWith('Bearer ')) return null;
  return raw.slice('Bearer '.length);
}

/**
 * Requests whose warnings the page renders itself.
 *
 * Set `suppressWarningToast` on a call's config when the SCREEN already says
 * the same thing — today only the visitor pre-registration, which shows its
 * warning inline BEFORE the record is created. That inline panel is the only
 * thing that can prevent the mistake; the toast reports it afterwards. Both
 * are wanted, but not the same sentence twice in one flow.
 *
 * The default is to show. Opting out is the exception, visible in the diff at
 * the call site, and a new endpoint's warnings surface with no frontend work
 * at all — which is the point of doing this here rather than at 304 call sites.
 */
export interface WarningAwareConfig extends AxiosRequestConfig {
  suppressWarningToast?: boolean;
  /** This write changes nothing on any screen, so do not re-read after it.
   *  See utils/refreshAfterWrite.ts; the URL list there is the usual way. */
  skipRefresh?: boolean;
}

/** Methods whose response may raise a warning toast.
 *
 * Mutating only. A GET runs on every page load and every react-query refetch,
 * so a toast per read is noise, and noise is how people learn to dismiss a
 * warning without reading it. If a GET ever genuinely needs to warn, that is a
 * reason to revisit this rather than to widen it quietly.
 */
const WARNING_METHODS = new Set(['post', 'put', 'patch', 'delete']);

api.interceptors.response.use(
  (response) => {
    // Every helper in this file goes through this instance, so this is the one
    // place a warning has to be handled for all of them.
    const config = response.config as WarningAwareConfig & { method?: string };
    if (!config?.suppressWarningToast && WARNING_METHODS.has((config?.method ?? '').toLowerCase())) {
      for (const message of warningsFrom(response.data)) {
        showWarning(message);
        // Parked as well as shown: a handler that redirects the whole document
        // straight after this would otherwise destroy the toast mid-sentence.
        queueWarning(message);
      }
    }
    // New Bug Auto Refresh: a confirmed write re-reads what the screen shows,
    // here, once, for every helper — rather than at each of several hundred
    // call sites, some of which forgot. utils/refreshAfterWrite.ts has the why.
    if (WARNING_METHODS.has((config?.method ?? '').toLowerCase())) {
      notifyWrite(config?.url, { skipRefresh: config?.skipRefresh });
    }
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Skip refresh for login/register/refresh endpoints
    const skipPaths = ['/auth/login', '/auth/register', '/auth/refresh', '/auth/forgot-password', '/auth/reset-password'];
    const isAuthEndpoint = skipPaths.some((p) => originalRequest?.url?.includes(p));
    // Reception R4: a tablet has no refresh token. A refused device token means
    // revoked or a new code: back to the code screen, never a person's login.
    if (isKioskDevicePath(originalRequest?.url)) {
      if (error.response?.status === 401 && !originalRequest?.url?.endsWith('/login')) clearKioskDevice();
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true;

      // Sent with a token that has since been replaced — a refresh finished
      // while this request was in flight. Retry with the current one; refreshing
      // again would spend the new refresh token for nothing (EXT-23).
      const sentWith = tokenSentWith(originalRequest);
      const current = getAccessToken();
      if (sentWith && current && sentWith !== current) {
        originalRequest.headers.Authorization = `Bearer ${current}`;
        return api(originalRequest);
      }

      if (!getRefreshToken()) {
        clearAuth();
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        const newAccessToken = await refreshAccessToken();
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        clearAuth();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(withBackendDetail(error));
  },
);

/**
 * Put the backend's `detail` into `error.message`.
 *
 * Axios rejects with an AxiosError whose `.message` is the status line —
 * "Request failed with status code 409" — while the sentence the API actually
 * wrote sits at `response.data.detail`. Roughly 180 handlers across the app do
 * `e instanceof Error ? e.message : 'unknown'`, so every one of them showed the
 * status line and threw away the explanation: a user re-sending a bill saw
 * "Request failed with status code 409" instead of "This bill was already sent
 * (2026-08-07). Call again with resend=true to send it deliberately."
 *
 * Fixing it here rather than at ~180 call sites: the defect is one layer's, the
 * handlers were reasonable. `response` is left untouched, so routeRequestError
 * and the handlers that read `response.data.detail` directly keep working —
 * this only improves what `.message` says.
 *
 * FastAPI emits `detail` as a string for HTTPException and as a list of
 * {loc, msg} for 422 validation errors; both are handled.
 */
function withBackendDetail(error: AxiosError): AxiosError {
  const detail = (error.response?.data as { detail?: unknown } | undefined)?.detail;

  if (typeof detail === 'string' && detail.trim()) {
    error.message = detail;
    return error;
  }

  if (Array.isArray(detail)) {
    // Shared with routeRequestError so one rule produces both. This used to
    // join the `msg` values and drop `loc` entirely, so every missing-field 422
    // in the app said "Field required" and named no field — TC_12's stars
    // returned exactly that.
    const msg = formatDetailList(detail);
    if (msg) error.message = msg;
    return error;
  }

  // A structured refusal — `detail: {message, ...}` — as bulk desk booking
  // sends with the per-line verdicts beside it. Its sentence is the message;
  // the rest stays on response.data.detail for the screen that wants it.
  // Without this the toast said "Request failed with status code 409".
  if (detail && typeof detail === 'object') {
    const m = (detail as { message?: unknown }).message;
    if (typeof m === 'string' && m.trim()) error.message = m;
    return error;
  }

  // No detail (network failure, 502 from the platform, HTML error page). The
  // axios message is genuinely the best available and is left alone.
  return error;
}

// ---------------------------------------------------------------------------
// Typed helpers
// ---------------------------------------------------------------------------

export async function apiGet<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const response = await api.get<ApiResponse<T>>(url, config);
  return response.data.data;
}

/** GET, keeping the envelope — for a read whose `message` is part of the
 *  answer (GET /hybrid/whos-in says whose list it is). See apiPostEnvelope. */
export async function apiGetEnvelope<T = unknown>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<ApiResponse<T>> {
  const response = await api.get<ApiResponse<T>>(url, config);
  return response.data;
}

export async function apiGetPaginated<T>(
  url: string,
  params?: Record<string, unknown>,
): Promise<PaginatedResponse<T>> {
  const response = await api.get<PaginatedResponse<T>>(url, { params });
  return response.data;
}

export async function apiPost<T, D = unknown>(
  url: string,
  data?: D,
  config?: WarningAwareConfig,
): Promise<T> {
  // A FormData must not inherit this instance's `Content-Type: application/json`.
  // axios reads that header and takes it literally: transformRequest turns a
  // FormData into `JSON.stringify(formDataToJSON(data))` whenever the content
  // type says JSON. A File has no enumerable own properties, so a posted file
  // serialised to `{}` — the body went out as {"csv_file":{}} and the server
  // saw no file at all, answering 422 "Field required". Nothing failed
  // loudly; the upload simply never contained the file.
  //
  // Two call sites hit this (both asset CSV imports) and the feature had never
  // once worked. Handling it HERE rather than only at those call sites is the
  // point: `apiUpload` exists and is the better-named choice, but the next
  // person to reach for the obvious `apiPost` with a FormData gets a working
  // request instead of a silent one.
  const isFormData = typeof FormData !== 'undefined' && data instanceof FormData;
  const merged: AxiosRequestConfig | undefined = isFormData
    ? { ...config, headers: { ...(config?.headers ?? {}), 'Content-Type': 'multipart/form-data' } }
    : config;
  const response = await api.post<ApiResponse<T>>(url, data, merged);
  return response.data.data;
}

/** POST returning the whole envelope, not just `data`.
 *
 * `apiPost` unwraps to `response.data.data` and discards `message`, which is
 * fine when the payload is the answer. It is not fine when the MESSAGE is the
 * answer: the forgot-password endpoint deliberately returns no data and says
 * "If that email exists, a reset link has been sent." — wording chosen so it
 * neither confirms an address nor promises a delivery it cannot guarantee.
 * Throwing that away is how the page came to invent a claim of its own.
 */
export async function apiPostEnvelope<T = unknown, D = unknown>(
  url: string,
  data?: D,
  config?: WarningAwareConfig,
): Promise<ApiResponse<T>> {
  const response = await api.post<ApiResponse<T>>(url, data, config);
  return response.data;
}

export async function apiPut<T, D = unknown>(
  url: string,
  data?: D,
  config?: WarningAwareConfig,
): Promise<T> {
  const response = await api.put<ApiResponse<T>>(url, data, config);
  return response.data.data;
}

/**
 * PUT, keeping the envelope.
 *
 * apiPut discards `message`, and for a decision route the message IS the
 * answer — approving an invoice reports which figure it was checked against,
 * and refusing one reports which figures disagreed. Sibling of
 * apiPostEnvelope / apiPatchEnvelope / apiDeleteEnvelope, which existed for
 * exactly this reason while PUT did not.
 */
export async function apiPutEnvelope<T = unknown, D = unknown>(
  url: string,
  data?: D,
  config?: WarningAwareConfig,
): Promise<ApiResponse<T>> {
  const response = await api.put<ApiResponse<T>>(url, data, config);
  return response.data;
}

export async function apiPatch<T, D = unknown>(
  url: string,
  data?: D,
  config?: WarningAwareConfig,
): Promise<T> {
  const response = await api.patch<ApiResponse<T>>(url, data, config);
  return response.data.data;
}

/**
 * PATCH, keeping the envelope.
 *
 * Some handlers put a WARNING in `message` rather than in the data — the desk
 * update endpoint returns "N booking(s) on or after today remain on this desk"
 * there when you deactivate one. apiPatch discards the envelope, so that
 * sentence had never once reached a person.
 */
export async function apiPatchEnvelope<T = unknown, D = unknown>(
  url: string,
  data?: D,
  config?: WarningAwareConfig,
): Promise<ApiResponse<T>> {
  const response = await api.patch<ApiResponse<T>>(url, data, config);
  return response.data;
}

/** DELETE, keeping the envelope — the delete handlers report a booking count
 *  and a warning in `message`, and apiDelete discards both. */
export async function apiDeleteEnvelope<T = unknown>(
  url: string,
  config?: WarningAwareConfig,
): Promise<ApiResponse<T>> {
  const response = await api.delete<ApiResponse<T>>(url, config);
  return response.data;
}

export async function apiDelete<T = void>(url: string, config?: WarningAwareConfig): Promise<T> {
  const response = await api.delete<ApiResponse<T>>(url, config);
  return response.data.data;
}

/**
 * Upload a file.
 *
 * `timeoutMs` opts this call out of the 30s page-load budget, for the same
 * reason a bulk write does — see BULK_WRITE_TIMEOUT_MS. An IMPORT is the case
 * that needs it most: the server does per-row work, and when the browser gives
 * up first the request is not cancelled, it is merely unheard. The rows are
 * still written and the RESPONSE IS LOST — which for the user import means
 * accounts created whose only set-password links were in that response.
 */
export async function apiUpload<T>(
  url: string,
  formData: FormData,
  onProgress?: (percent: number) => void,
  timeoutMs?: number,
): Promise<T> {
  const response = await api.post<ApiResponse<T>>(url, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    ...(timeoutMs ? { timeout: timeoutMs } : {}),
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress(percent);
      }
    },
  });
  return response.data.data;
}

// ---------------------------------------------------------------------------
// Binary file download helper (fetch → blob → anchor.click)
// ---------------------------------------------------------------------------

/**
 * Lift the backend's `detail` out of a failed download response.
 *
 * The download helpers use raw `fetch`, so they never pass through the axios
 * interceptor that does this for every other call — see withBackendDetail.
 * Eight callers throw `new Error('Export failed (404)')` and the sentence the
 * API actually wrote is discarded: the label endpoint says "No active assets
 * found for those ids. Decommissioned and disposed assets are not labelled."
 * and the user saw a bare 404.
 *
 * A failed download is JSON even though a successful one is a blob, so the
 * body is read as text and parsed defensively — an HTML error page from the
 * platform, or an empty body, must not turn a useful status into a crash.
 */
async function downloadErrorMessage(response: Response): Promise<string> {
  const fallback = `Export failed (${response.status})`;
  let body: unknown;
  try {
    const text = await response.text();
    if (!text.trim()) return fallback;
    body = JSON.parse(text);
  } catch {
    return fallback;
  }
  const detail = (body as { detail?: unknown } | null)?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  // Same rule as the interceptor, so a 422 names its field here too.
  if (Array.isArray(detail)) return formatDetailList(detail) || fallback;
  return fallback;
}

/** The downloads hand back the response's headers, so a caller can read what
 *  the server says ABOUT the file — e.g. X-Labels-Skipped-Ids, the assets a
 *  label sheet left out (Extra R4-8). Callers that ignore it are unchanged. */
export async function downloadFile(endpoint: string, filename: string): Promise<Headers> {
  const token = getAccessToken();
  const response = await fetch(`${API_BASE_URL}/api${endpoint}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) throw new Error(await downloadErrorMessage(response));
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  return response.headers;
}

export async function downloadFilePost(
  endpoint: string,
  payload: Record<string, unknown>,
  filename: string,
): Promise<Headers> {
  const token = getAccessToken();
  const response = await fetch(`${API_BASE_URL}/api${endpoint}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(await downloadErrorMessage(response));
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  return response.headers;
}

/**
 * Save a file the API returned INSIDE a JSON envelope as base64 — the shape
 * POST /leases/payment-runs/{id}/export answers with, because the same call
 * also changes the run's status and reports it.
 *
 * Deep-test LEA-025: the Payment Runs page threw `file_base64` away, so Export
 * downloaded nothing and the toast said "Done". Throws when there is no file,
 * so a caller cannot report a download that did not happen.
 */
export function saveBase64File(base64: string | null | undefined, filename: string,
  contentType = 'application/octet-stream'): void {
  if (!base64) throw new Error('The server sent no file.');
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  const url = URL.createObjectURL(new Blob([bytes], { type: contentType }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * HTTP status of a rejected request, or 0 when there was no response at all
 * (network failure, CORS, abort).
 *
 * Mainly for TanStack `retry` predicates: the default retries three times, which
 * is right for a flaky connection and wrong for a 403 — the answer will not
 * change, and the query sits in `isLoading` through every attempt, so the UI
 * shows a loading state for something that already failed.
 */
export function httpStatus(err: unknown): number {
  return (err as { response?: { status?: number } } | null)?.response?.status ?? 0;
}

/**
 * Pull a list out of a response body, whether it arrives bare, under `data`,
 * or under `data.data`. Several pages carry a private copy of this; new callers
 * should use this one rather than adding another.
 */
export function unwrapList<T>(d: unknown): T[] {
  if (!d) return [];
  if (Array.isArray(d)) return d as T[];
  if (typeof d === 'object') {
    const inner = (d as { data?: unknown }).data;
    if (Array.isArray(inner)) return inner as T[];
    if (inner && typeof inner === 'object') {
      const deeper = (inner as { data?: unknown }).data;
      if (Array.isArray(deeper)) return deeper as T[];
    }
  }
  return [];
}

/**
 * Pull `total` out of the same envelopes unwrapList understands.
 * Returns null when the endpoint does not paginate — there is then nothing to
 * be truncated by, and one request is the whole answer.
 */
export function unwrapTotal(d: unknown): number | null {
  if (!d || typeof d !== 'object' || Array.isArray(d)) return null;
  const direct = (d as { total?: unknown }).total;
  if (typeof direct === 'number') return direct;
  const inner = (d as { data?: unknown }).data;
  if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
    const nested = (inner as { total?: unknown }).total;
    if (typeof nested === 'number') return nested;
  }
  return null;
}

/** One page of a list endpoint: the rows, and how many there are ALTOGETHER.
 *
 * THE BUG THIS EXISTS FOR. `apiGet` already unwraps to `response.data.data`, so
 * a paginated endpoint hands back `{ data: [...], total }`. Two callers then
 * wrote the defensive idiom `(r as any)?.data ?? r` on top of that — and
 * because a page's inner key is ALSO called `data`, it unwraps one level too
 * far onto the row array. `array.total` is undefined, so:
 *
 *   * the Admin "Audit Events" card returned null and rendered an em-dash
 *     against 247 real rows, on every deployment, since it was written;
 *   * the "Sites" card fell through to `array.length` — the PAGE length, right
 *     only while an organisation has fewer buildings than the default page
 *     size of 20. Spaciora has 15.
 *
 * The idiom is harmless when the caller wants the rows, because `Array.isArray`
 * rescues it. It is wrong exactly when the caller wants the total, which is why
 * it survived so long. Read a page through this instead of unwrapping by hand.
 *
 * `total` is null when the endpoint does not report one. That is NOT the same
 * as `rows.length`, which is only the page — a caller that needs a number must
 * decide what to show rather than be handed a truncated count as if it were a
 * total. A bare array is the exception: it is the whole answer by definition,
 * so its length IS its total.
 */
export interface Page<T> {
  rows: T[];
  total: number | null;
}

export function unwrapPage<T>(payload: unknown): Page<T> {
  const rows = unwrapList<T>(payload);
  const reported = unwrapTotal(payload);
  return {
    rows,
    total: reported ?? (Array.isArray(payload) ? rows.length : null),
  };
}

/** The endpoint maximum: PaginationParams declares limit as `le=200`. */
export const MAX_PAGE_LIMIT = 200;

/**
 * A hard stop, so a mistake here is a slow picker rather than a hung browser.
 * Ten requests at the endpoint maximum. Past this the honest fix is a
 * server-side search, and `truncated` is how the UI gets told to say so.
 */
export const MAX_FETCH_ALL_RECORDS = 10 * MAX_PAGE_LIMIT;

export interface AllPages<T> {
  rows: T[];
  /** What the server says exists. null when the endpoint does not paginate. */
  total: number | null;
  /** True when rows is a SUBSET — the caller must say so rather than imply a full list. */
  truncated: boolean;
}

/**
 * Every page of a paginated list endpoint, not just the first one.
 *
 * THE BUG THIS EXISTS FOR: `PaginationParams` defaults `limit` to 20, so any
 * picker that forgets it — or passes the wrong parameter name — silently shows
 * the first 20 of whatever there is. Nobody notices, because twenty options in
 * a dropdown look exactly like a full list. It has now happened three times:
 * the room list on SpacePage, and both pickers on the IoT Reports tab, where
 * `?page_size=200` was dropped on the floor because the parameter is `limit`
 * and 54 machines showed up as 20.
 *
 * WHY FETCH ALL RATHER THAN PAGINATE ON SCROLL OR SEARCH SERVER-SIDE. The
 * question a picker answers is "which of my plant's machines", and a plant is
 * bounded in the hundreds. 54 machines is one request; 500 is three; both are
 * faster than a scroll-paginated combobox and keep client-side search working
 * over the WHOLE set rather than over whichever page happens to be loaded.
 * Server-side search is the right answer only once the set stops fitting in
 * memory sensibly, and MAX_FETCH_ALL_RECORDS is where that line is drawn.
 *
 * Callers MUST honour `truncated` — a picker showing a subset in silence is
 * the bug this helper was written to remove, not a smaller version of it.
 */
export async function fetchAllPages<T>(
  path: string,
  opts: { maxRecords?: number } = {},
): Promise<AllPages<T>> {
  const maxRecords = opts.maxRecords ?? MAX_FETCH_ALL_RECORDS;

  // Strip any page/limit the caller left on the path. Appending ours on top
  // would send the parameter twice, and which copy wins is a detail of the
  // server's query parser rather than anything this function controls.
  const [rawPath = path, rawQuery = ''] = path.split('?');
  const params = new URLSearchParams(rawQuery);
  params.delete('page');
  params.delete('limit');
  const base = params.toString() ? `${rawPath}?${params.toString()}` : rawPath;
  const join = base.includes('?') ? '&' : '?';
  const rows: T[] = [];
  let total: number | null = null;

  for (let page = 1; ; page += 1) {
    const body = await apiGet<unknown>(
      `${base}${join}page=${page}&limit=${MAX_PAGE_LIMIT}`);
    const batch = unwrapList<T>(body);
    if (page === 1) total = unwrapTotal(body);
    rows.push(...batch);

    // A short page is the last page. Checked BEFORE the total, so an endpoint
    // that reports no total still terminates after one request.
    if (batch.length < MAX_PAGE_LIMIT) break;
    if (total !== null && rows.length >= total) break;
    if (rows.length >= maxRecords) break;
  }

  return {
    rows,
    total,
    truncated: total !== null && rows.length < total,
  };
}

/**
 * fetchAllPages when the caller only wants the rows.
 *
 * Most list reads in this app are `unwrapList(await apiGet(path))` feeding a
 * table or a picker; this is the same shape, one page-limit bug lighter. Use
 * fetchAllPages directly wherever the UI should SAY that a load was capped.
 */
export async function fetchAllRows<T>(
  path: string,
  opts: { maxRecords?: number } = {},
): Promise<T[]> {
  return (await fetchAllPages<T>(path, opts)).rows;
}

export default api;
